import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import type { DashboardItem } from '@shared/types';

interface Props {
  item: DashboardItem;
  hasPendingComments: boolean;
  onRequestChanges: () => Promise<void>;
  onPublish: () => Promise<void>;
  readyCount?: number;
  totalPages?: number;
}

export function ActionButtons(props: Props) {
  const { item, readyCount, totalPages } = props;
  const showReadyCount = totalPages != null && totalPages > 1;
  const isReview = item.status === 'needs-review';
  const requestEnabled = isReview && props.hasPendingComments;
  const publishEnabled = isReview;
  const baseLabel = item.baseBranch === 'main'
    ? 'Publish'
    : `Publish to ${item.baseBranch}`;
  const statusBlock =
    item.status === 'building' ? 'Claude is updating the article. Try again in a minute.' :
    item.status === 'publishing' ? 'Publishing is already in progress.' :
    item.status === 'drafting' ? 'No preview yet.' :
    item.status === 'failed' ? 'Triage the failing check on GitHub first.' :
    '';
  const requestTooltip = statusBlock ||
    (!props.hasPendingComments ? 'Add at least one comment first.' : '');
  const publishTooltip = statusBlock;

  const [publishOpen, setPublishOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const wrap = (fn: () => Promise<void>) => async () => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  if (item.status === 'published') return null;

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2">
        {showReadyCount && (
          <span className="text-xs text-muted-foreground">{readyCount ?? 0}/{totalPages} ready</span>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant="outline"
                size="sm"
                disabled={!requestEnabled || busy}
                onClick={wrap(props.onRequestChanges)}
              >
                Request changes
              </Button>
            </span>
          </TooltipTrigger>
          {requestTooltip && <TooltipContent>{requestTooltip}</TooltipContent>}
        </Tooltip>

        <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  size="sm"
                  disabled={!publishEnabled || busy}
                  onClick={() => setPublishOpen(true)}
                >
                  {baseLabel}
                </Button>
              </span>
            </TooltipTrigger>
            {publishTooltip && <TooltipContent>{publishTooltip}</TooltipContent>}
          </Tooltip>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{baseLabel}?</DialogTitle>
              <DialogDescription>
                This requests publication. The automation will merge the PR if validation has passed.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPublishOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  setPublishOpen(false);
                  await wrap(props.onPublish)();
                }}
              >
                {baseLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
